import React from 'react';
import { Box, Button, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { Delete, Refresh } from '@mui/icons-material';
import { SpiffsEntry, isMacConfigName } from '../flash/serialSession';
import { useUsbSession } from '../flash/UsbSessionContext';
import { useI18n } from '../i18n';

interface UsbFileListProps {
  selectedPath?: string;
  onOpen: (file: SpiffsEntry) => void;
  disabled?: boolean;
}

const UsbFileList: React.FC<UsbFileListProps> = ({ selectedPath, onOpen, disabled }) => {
  const usb = useUsbSession();
  const { t } = useI18n();
  const locked = disabled || usb.busy;

  if (!usb.connected) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('usb.listHint')}
      </Typography>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="subtitle2">{t('usb.onDevice')}</Typography>
        <Button size="small" startIcon={<Refresh />} onClick={() => usb.refreshFiles()} disabled={locked}>
          {t('common.refresh')}
        </Button>
      </Box>
      <List dense sx={{ maxHeight: 220, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1 }}>
        {usb.files.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
            {t('usb.empty')}
          </Typography>
        )}
        {usb.files.map((file) => (
          <ListItemButton
            key={file.path}
            selected={selectedPath === file.path}
            disabled={locked || file.isDir}
            onClick={() => !file.isDir && onOpen(file)}
          >
            <ListItemText
              primary={file.name}
              secondary={
                file.isDir
                  ? t('common.directory')
                  : `${t('common.bytesCount', { count: file.size })}${isMacConfigName(file.name) ? ` · ${t('common.oldMacConfig')}` : ''}`
              }
              primaryTypographyProps={{ noWrap: true }}
            />
            {!file.isDir && (
              <Button
                size="small"
                color="error"
                startIcon={<Delete />}
                disabled={locked}
                onClick={(event) => {
                  event.stopPropagation();
                  if (window.confirm(t('usb.deleteConfirm', { path: file.path }))) {
                    usb.removeFile(file.path).catch(() => undefined);
                  }
                }}
              >
                {t('common.delete')}
              </Button>
            )}
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
};

export default UsbFileList;
